/* eslint-disable */
// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core'
import React, { useContext, useState, useRef, useEffect, useCallback } from 'react'
import { Query } from 'react-apollo'
import cx from 'classnames'
import type { GridItem } from '../../types'
import GridApi from '../../components/GridApi'
import Curtain from '../../components/Curtain'
import PageContainer from '../../components/PageContainer'
import ActionsHeader from '../../components/ActionsHeader'
import ModuleQuery from '../../components/ModuleQuery'
import Row from '../../components/Row'
import Col from '../../components/Col'
import Notifications from '../../components/Notifications'
import ContentChecker from '../../components/ContentChecker'
import BlockingStatus from '../../components/BlockingStatus'
import GridApiController from '../../controllers/GridApiController'
import RefetchController from '../../controllers/RefetchController'
import userSettingsQuery from '../../graphql/queries/userSettings.query'
import orderContentQuery from '../../graphql/queries/orderContent.query'
import ActionsOrganizer from './components/ActionsOrganizer'
import LocalOrderItemsUpdater from './components/LocalOrderItemsUpdater'
import messages from '../../config/messages'
import formatters from './formatters'
import { calculateItemPrice, getDefaultUserSettings } from '../../utils'
import { OrderContentConsumer } from '../../context/orderContent.context'
import ActionAlerts from '../../components/ActionAlerts'
import { ActionsContext } from '../../context/actions.context';
import colors from '../../config/colors';
import { HistoryContext } from '../../context/history.context';
import { useMobileMediaQuery } from '../../config/mediaQuery';
import FullScreenContainer from '../../components/FullScreenContainer';
import SavedOrders from './mobile/SavedOrders'
import useMenuHandler from '../../hooks/useMenuHandler';

type Props = {
  isFullPage?: boolean,
  selectedRow?: GridItem,
  showGroupPanel?: boolean,
  paginated?: boolean,
  active: boolean,
}

const App = (props: Props) => {
  const moduleName = 'order';
  let refreshContent;

  const menuRef = useRef();
  const menuButtonRef = useRef();

  const {
    isFullPage = true,
    showGroupPanel = false,
    paginated = true,
    active,
    selectedRow
  } = props;

  const { setAppTitle, refresh, setRefresh, allScreens = [] } = useContext(ActionsContext);
  const history = useContext(HistoryContext);
  const isMobile = useMobileMediaQuery();
  const [displayMenu, setDisplayMenu] = useState(false);
  const [activeOrder, setActiveOrder] = useState(false);
  const [aggregateTotal, setAggregateTotal] = useState(0);
  const [displayCurrentOrder, setDisplayCurrentOrder] = useState(true);
  const [handlerAdded, setHandlerAdded] = useState(false);

  let itemHistory = null;
  let clearOrderType = null;

  const setItemHistory = (item) => {
    itemHistory = item;
  }

  const grandTotalRowCustomContent = (item: GridItem) =>
    calculateItemPrice(item)

  const changeDisplayMenu = (status) => {
    setDisplayMenu(status);
  }

  const changeCurrentOrder = (value, title) => {
    setDisplayCurrentOrder(value);
    setAppTitle(title);
  }

  useEffect(() => {
    isMobile && setAppTitle('Order');
    return () => {
      isMobile && clearOrderType && clearOrderType();
    }
  }, [])

  const menuHandler = useCallback((e) => {
    const datePicker = document.getElementsByClassName('wj-inputdate-dropdown')[0];
    const modal = document.getElementsByClassName('modal')[0]
    const modalOpen = modal && modal.classList.contains('in')
    if (menuRef.current 
      && menuButtonRef.current 
      && !menuRef.current.contains(e.target) //checks if the menu is not being tapped on
      && !menuButtonRef.current.contains(e.target) //checks if the menu button is not being tapped on
      && !modalOpen
      && !datePicker
    ) {
      setDisplayMenu(value => {
        if (value) {
          return false;
        }
        return value;
      });
    }
  }, []);

  useMenuHandler(menuHandler, active && allScreens.length === 0, setHandlerAdded);

  return (
    <OrderContentConsumer>
      {({
        selectedOrderType,
        onChangeOrderType,
        selectedOrderDate,
        onChangeOrderDate,
        orderItems,
        onChangeOrderItems,
        hasLocalOrderItems,
        hasUnsavedOrderType,
        clearSelectedOrderType,
        clearSelectedOrderTypeFull,
        resetCollection,
        removedItem,
        selectedOrderTypeFull
      }: any) => {
        if (isMobile && clearOrderType === null) {
          clearOrderType = clearSelectedOrderType
        }
        return (
          <PageContainer
            className={cx({
              'page--is-full-page': isFullPage,
              'page--is-nested': !isFullPage,
            })}
            isFullPage={isFullPage}
            active={active}
            savedOrders={!displayCurrentOrder}
          >
            <FullScreenContainer />
            {displayCurrentOrder ?
              <ModuleQuery
                query={orderContentQuery}
                variables={{
                  // `selectedOrderType` won't exist on page load, so this query
                  // will use the value saved on the Active Order. It will only
                  // exists when the user manually selected another order type
                  OrderType: selectedOrderType,
                  // Same as above but for the Order's date.
                  OrderDate: selectedOrderDate,
                  removedItem,
                }}
                flush={resetCollection}
              >
                {({
                  data: moduleData,
                  loading: moduleLoading,
                  refetch: moduleRefetch,
                  refetching: moduleRefetching,
                  refetched: moduleRefetched,
                }) => (
                  <Query
                    query={userSettingsQuery}
                    variables={{
                      moduleName: moduleName,
                    }}
                  >
                    {({ data: dataGrid, loading }: any) => {
                      if (!dataGrid || loading) {
                        return (
                          <BlockingStatus
                            excludeHeader
                            active
                            container={document.body}
                            text="Loading..."
                          />
                        )
                      }

                      const userSettings =
                        dataGrid && dataGrid.userSettings
                          ? {
                            columnsMapping: dataGrid.userSettings.columns,
                            frozenColumns: dataGrid.userSettings.frozenColumns,
                            groups: dataGrid.userSettings.groups,
                            sorting: dataGrid.userSettings.sorting,
                          }
                          : {}

                      if (typeof refreshContent !== 'function') {
                        refreshContent = moduleRefetch
                      }

                      const defaultUserSettings = getDefaultUserSettings(dataGrid)
                      if (refresh) {
                        moduleRefetch()
                        setRefresh(false)
                      }
                      return (
                        <React.Fragment>
                          <GridApiController
                            data={
                              moduleData.orderContent &&
                              moduleData.orderContent.Det
                            }
                            loading={loading || moduleLoading}
                            refetching={moduleRefetching}
                            refetched={moduleRefetched}
                            refetch={refreshContent}
                            defaultColumns={defaultUserSettings.defaultColumns}
                            defaultSorting={defaultUserSettings.defaultSorting}
                            defaultGroupPanels={
                              defaultUserSettings.defaultGroupPanels
                            }
                            paginated={paginated}
                            active={active}
                            orderItems={orderItems}
                            onChangeOrderItems={onChangeOrderItems}
                            hasLocalOrderItems={hasLocalOrderItems}
                            forceRefreshAfterUpdate
                            allowRemoval
                            allowAddition
                            {...userSettings}
                          >
                            {({
                              selectedItems,
                              gridUpdated,
                              collection,
                              columnsMapping,
                              currentItem,
                              onGridUpdated,
                              onGridRef,
                              onGridWillUnmount,
                              onColSorted,
                              onColResized,
                              onColDragged,
                              onColFreezed,
                              onRowFreezed,
                              onRowSelected,
                              onColumnsMappingChange,
                              onCurrentItemChanged,
                              onGroupPanelChanged,
                              onClearSelection,
                              onItemAdded,
                              itemAddedStatus,
                              orderCreateStatus,
                              orderRemoveStatus,
                              onOrderRemoved,
                              onOrderCreated,
                              onNotesSaved,
                              onItemUpdated,
                              saveStatus,
                              onViewSaved,
                              onViewReset,
                              grid,
                              userViewState,
                              refetching,
                              hasAllColumnsHidden,
                              frozenColumns,
                              refetch,
                              isLoading,
                              isRefetching,
                              hasDataToDisplay,
                              clearCollectionAndResetChangedOrderItems,
                              playResetAnimation,
                              handleResetAnimation,
                              setFlexGridFilter,
                              clearInternalFilters,
                              toggleMultiSort,
                              multiColumnSort
                            }) => {
                              /**
                               * if the Order module was loaded when there wasn't any data loaded
                               * then we need to force a refetch to get that data.
                               */
                              const refetchOnItemAdded =
                                !hasDataToDisplay &&
                                orderItems &&
                                orderItems.totalItemCount
                              const order = moduleData.orderContent;
                              const orderCreated = order && order.Account && order.Account.AcctNo;

                              return (
                                <React.Fragment>
                                  <LocalOrderItemsUpdater
                                    condition={moduleRefetched}
                                    collection={
                                      moduleData.orderContent &&
                                      moduleData.orderContent.Det
                                    }
                                    updater={onChangeOrderItems}
                                  />
                                  <RefetchController
                                    condition={[
                                      selectedOrderType,
                                      refetchOnItemAdded,
                                      resetCollection,
                                    ]}
                                    refetch={refreshContent}
                                    listenForRefetchEvent
                                    checkForFefetch={() => {
                                      if (!hasDataToDisplay) {
                                        refreshContent()
                                      }
                                    }}
                                  />
                                  <Notifications
                                    itemAddedStatus={itemAddedStatus}
                                    orderCreateStatus={orderCreateStatus}
                                    orderRemoveStatus={orderRemoveStatus}
                                    saveStatus={saveStatus}
                                  />
                                  <ActionAlerts
                                    moduleName={moduleName}
                                    namespace={'det'}
                                    collection={collection && collection.items}
                                  />
                                  <ActionsHeader
                                    grid={grid}
                                    collection={collection}
                                    defaultColumnsMapping={
                                      defaultUserSettings.defaultColumns
                                    }
                                    columnsMapping={columnsMapping}
                                    selectedItems={selectedItems}
                                    moduleName={moduleName}
                                    saveStatus={saveStatus}
                                    loading={isLoading || isRefetching}
                                    onViewSaved={onViewSaved}
                                    onViewReset={onViewReset}
                                    onNotesSaved={onNotesSaved}
                                    onOrderCreated={onOrderCreated}
                                    onColumnsMappingChange={
                                      onColumnsMappingChange
                                    }
                                    onClearSelection={onClearSelection}
                                    onOrderRemoved={onOrderRemoved}
                                    userViewState={userViewState}
                                    refetch={refetch}
                                    hasAllColumnsHidden={hasAllColumnsHidden}
                                    hasDataToDisplay={hasDataToDisplay}
                                    frozenColumns={frozenColumns}
                                    currentItem={currentItem}
                                    onItemAdded={onItemAdded}
                                    itemAddedStatus={itemAddedStatus}
                                    defaultSettings={defaultUserSettings}
                                    handleResetAnimation={handleResetAnimation}
                                    useLegacyColumnsManager={true}
                                    toggleMultiSort={toggleMultiSort}
                                    multiColumnSort={multiColumnSort}
                                  >
                                    {(actions) => {
                                      const { isOneRowSelected } = actions;
                                      return (
                                        <div ref={menuRef}>
                                          <Row
                                            css={{
                                              justifyContent: 'space-between',
                                              padding: '0 10px',
                                              ...(isMobile && {
                                                position: 'absolute',
                                                bottom: 0,
                                                boxShadow:
                                                  '0px -4px 4px 0px #ACACAC40',
                                                zIndex: 100,
                                                background: '#fff',
                                                borderRadius: '8px 8px 0px 0px',
                                                width: '100%',
                                                display: displayMenu || isOneRowSelected
                                                  ? 'flex'
                                                  : 'none',
                                                justifyContent: 'center',
                                                height: (displayMenu && '300px') || (isOneRowSelected && '144px'),
                                              }),
                                            }}
                                          >
                                            <Col css={{ width: '100%' }}>
                                              <ActionsOrganizer
                                                minHeight='80px'
                                                onItemUpdated={onItemUpdated}
                                                ShipTo={
                                                  moduleData.orderContent &&
                                                  moduleData.orderContent.ShipTo
                                                }
                                                Det={
                                                  moduleData.orderContent &&
                                                  moduleData.orderContent.Det
                                                }
                                                collection={collection}
                                                {...actions}
                                                onChangeOrderType={onChangeOrderType}
                                                selectedOrderType={selectedOrderType}
                                                onChangeOrderDate={onChangeOrderDate}
                                                clearSelectedOrderType={
                                                  clearSelectedOrderType
                                                }
                                                selectedOrderDate={selectedOrderDate}
                                                shouldHaveDatePicker={
                                                  moduleLoading
                                                    ? false
                                                    : moduleData.orderContent &&
                                                    moduleData.orderContent
                                                      .shouldHaveDatePicker
                                                }
                                                refetch={refreshContent}
                                                clearCollectionAndResetChangedOrderItems={
                                                  clearCollectionAndResetChangedOrderItems
                                                }
                                                moduleRefetch={moduleRefetch}
                                                clearInternalFilters={
                                                  clearInternalFilters
                                                }
                                                setActiveOrder={setActiveOrder}
                                                setItemHistory={setItemHistory}
                                                displayRowMenu={isOneRowSelected}
                                                displayMenu={displayMenu}
                                                toggleMultiSort={toggleMultiSort}
                                              />
                                            </Col>
                                          </Row>
                                        </div>
                                      )
                                    }}
                                  </ActionsHeader>
                                  {isMobile && !orderCreated && !isLoading && (
                                    <div className="order-empty-container">
                                      <div className="order-empty">
                                        <div className="empty-title">
                                          No orders in cart
                                        </div>
                                        <div className="empty-message">
                                          Go to Accounts page to start creating
                                          order
                                        </div>
                                        <button
                                          className="empty-button"
                                          onClick={() =>
                                            history.push('/accounts')
                                          }
                                        >
                                          Go To Accounts
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  <ContentChecker
                                    isLoading={isLoading}
                                    isRefetching={isRefetching}
                                    hasAllColumnsHidden={hasAllColumnsHidden}
                                    hasDataToDisplay={hasDataToDisplay}
                                    collection={collection}
                                    noContentMsg={messages.order.noContent}
                                  >
                                    {() => {
                                      const gridApi = (
                                        <GridApi
                                          active={active}
                                          collection={collection}
                                          columnsMapping={columnsMapping}
                                          frozenColumns={frozenColumns}
                                          onRowSelected={onRowSelected}
                                          onColSorted={onColSorted}
                                          onColResized={onColResized}
                                          onColDragged={onColDragged}
                                          onColFreezed={onColFreezed}
                                          onRowFreezed={onRowFreezed}
                                          onGridUpdated={onGridUpdated}
                                          onGridWillUnmount={onGridWillUnmount}
                                          onCurrentItemChanged={
                                            onCurrentItemChanged
                                          }
                                          onGroupPanelChanged={
                                            onGroupPanelChanged
                                          }
                                          onClearSelection={onClearSelection}
                                          ref={onGridRef}
                                          refetching={isRefetching}
                                          hasAllColumnsHidden={
                                            hasAllColumnsHidden
                                          }
                                          hasDataToDisplay={hasDataToDisplay}
                                          isFullPage={isFullPage}
                                          showFrozenBars
                                          showGroupPanel={showGroupPanel}
                                          paginated={paginated}
                                          formatters={formatters}
                                          addGrandTotalRow
                                          grandTotalRowCustomContent={
                                            grandTotalRowCustomContent
                                          }
                                          playResetAnimation={playResetAnimation}
                                          setFlexGridFilter={setFlexGridFilter}
                                          gridHeight={isMobile && '113px'}
                                          setAggregateTotal={setAggregateTotal}
                                          menuRef={menuRef}
                                          isMobile={isMobile}
                                          handlerAdded={handlerAdded}
                                          multiColumnSort={multiColumnSort}
                                        />
                                      )
                                      const activeOrderAccount = activeOrder && activeOrder.Account;
                                      return isMobile ? (
                                        orderCreated &&
                                        <div className="order-details-container">
                                          <div className="order-details-header">
                                            <p>{activeOrderAccount && activeOrder.Account.AcctName}</p>
                                            <img
                                              ref={menuButtonRef}
                                              src='assets/3dots.svg'
                                              onClick={() => {
                                                changeDisplayMenu(!displayMenu)
                                              }}
                                            />
                                          </div>
                                          <div className="order-details-content">
                                            <p>Type: {activeOrderAccount && (selectedOrderTypeFull || activeOrder.OrderType)}</p>
                                            <p>Account #: {activeOrderAccount && order.Account.AcctNo}</p>
                                            <p>Account Address: {activeOrderAccount && activeOrder.Account.Address}</p>
                                            {hasDataToDisplay && <p>Order Details:</p>}
                                          </div>
                                          {!hasDataToDisplay &&
                                            <div className="order-details-empty">
                                              <p>Add items from Inventory or Item History</p>
                                              <a href='/inventory'>Go to Inventory</a>
                                              {itemHistory}
                                            </div>
                                          }
                                          {hasDataToDisplay && gridApi}
                                          {hasDataToDisplay &&
                                            <div className="order-details-bottom">
                                              <p>Total: </p>
                                              <p
                                                style={{
                                                  color: colors.mobileGreen,
                                                }}
                                              >
                                                ${aggregateTotal.toFixed(2)}
                                              </p>
                                            </div>}
                                        </div>
                                      ) : (
                                        <React.Fragment>
                                          <Curtain
                                            container={grid && grid.hostElement}
                                            active={active}
                                          />
                                          {gridApi}
                                        </React.Fragment>
                                      )
                                    }}
                                  </ContentChecker>
                                </React.Fragment>
                              )
                            }}
                          </GridApiController>
                        </React.Fragment>
                      )
                    }}
                  </Query>
                )}
              </ModuleQuery>
              :
              <SavedOrders />
            }
            {isMobile && (
              <div className="order-empty-buttons">
                <button onClick={() => changeCurrentOrder(true, 'Order')} style={{ color: displayCurrentOrder && colors.mobileGreen }}>
                  <img src={displayCurrentOrder ? 'assets/cart-green-active.svg' : 'assets/cart-green-inactive.svg'} />
                  Order
                </button>
                <button
                  onClick={() => {
                    changeCurrentOrder(false, 'Saved Orders')
                    clearSelectedOrderTypeFull();
                  }}
                  style={{ color: !displayCurrentOrder && colors.mobileGreen }}
                >
                  <img src={displayCurrentOrder ? 'assets/saved-inactive.svg' : 'assets/saved-active.svg'} />
                  Saved Orders
                </button>
              </div>
            )}
          </PageContainer>
        )
      }}
    </OrderContentConsumer>
  )
}

export default App
